import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { API_BASE_URL_SERVICE, RELEASE_NOTES_APP } from '../../kiene-core.config';
import { AppService } from '../../services/app/app.service';
import { KieneBackendApiService } from '../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../services/message-service/message.service';
import { KieneUtils } from '../../utils/KieneUtils';
import { Client } from '../client/classes/client';
import { ConfirmDialogInput, ConfirmDialogResult } from '../dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { GruppeTier } from '../gruppe-tier/classes/gruppe-tier';
import { ohneTierlisteKommentar, Tierliste } from '../gruppe-tier/classes/tierliste';
import { resolveTierlisteExtern } from '../gruppe-tier/tierliste-zielbetrieb-display.util';
import { Gruppe } from '../gruppen/classes/gruppe';
import { GruppeAbgangDialogInput } from '../gruppen/gruppe-abgang-dialog/gruppe-abgang-dialog';
import { GruppeAbgangDialogComponent } from '../gruppen/gruppe-abgang-dialog/gruppe-abgang-dialog.component';
import { GruppeAbgangService } from '../gruppen/gruppe-abgang/gruppe-abgang.service';
import {
    LISTE_ABSCHLIESSEN_STEPPER_HANDLER,
    ListeAbschliessenStepperHandler,
} from '../gruppen/gruppe-abgang/liste-abschliessen-stepper.handler';
import { TierlistenauswahlDialogInput } from '../gruppen/gruppe-abgang/tierlistenauswahl-dialog/tierlistenauswahl-dialog';
import { TierlistenauswahlDialogComponent } from '../gruppen/gruppe-abgang/tierlistenauswahl-dialog/tierlistenauswahl-dialog.component';
import {
    buildGruppeAuswaehlenDialogConfig,
    GruppeAuswaehlenDialogInput,
    GruppeAuswaehlenDialogResult,
} from '../gruppen/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog';
import { GruppeAuswaehlenDialogComponent } from '../gruppen/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog.component';
import { StammdatenService } from '../stammdaten/stammdaten.service';
import { AusstallenDialogInput, AusstallenDialogResult } from './ausstallen-dialog/ausstallen-dialog';
import { AusstallenDialogComponent } from './ausstallen-dialog/ausstallen-dialog.component';
import {
    AusstallenFrageDialogInput,
    AusstallenFrageDialogResult,
} from './ausstallen-frage-dialog/ausstallen-frage-dialog';
import { AusstallenFrageDialogComponent } from './ausstallen-frage-dialog/ausstallen-frage-dialog.component';
import {
    AbgangZielFlowType,
    AusstallenEmpfaengerDialogComponent,
    AusstallenEmpfaengerDialogResult,
} from './ausstallen-empfaenger-dialog/ausstallen-empfaenger-dialog.component';
import {
    TIERLISTE_INTERNAL_IMPORT_HANDLER,
    TierlisteInternalImportHandler,
} from './tierliste-internal-import.handler';
import { TierlisteSchlachthofAuswahlDialogComponent } from './tierliste-schlachthof-auswahl-dialog/tierliste-schlachthof-auswahl-dialog.component';

export type AusstallenMobilePhase = 'finish';

export interface AusstallenMobilePageContext {
    input: AusstallenDialogInput;
    /** `finish` = Liste abschließen after mobile scan step. */
    mobilePhase?: AusstallenMobilePhase;
    gruppe?: Gruppe;
    gruppeAbgangAlsDialog?: boolean;
    tiere?: GruppeTier[];
    onComplete?: (result: AusstallenDialogResult) => void;
}

@Injectable({ providedIn: 'root' })
export class AusstallenService {
    private importDialogOffen = false; // Schutz vor mehrfachen Dialog-Öffnungen
    private mobilePageContext: AusstallenMobilePageContext | null = null;
    /** Kundenportal: Ausstallen form opens after scan step (not before). */
    private pendingMobileAusstallenFinish: AusstallenMobilePageContext | null = null;

    constructor(
        private dialog: MatDialog,
        private router: Router,
        private appService: AppService,
        private abgangService: GruppeAbgangService,
        private messageService: MessageService,
        private stammdatenService: StammdatenService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl,
        private api: KieneBackendApiService,
        @Optional()
        @Inject(TIERLISTE_INTERNAL_IMPORT_HANDLER)
        private internalImportHandler?: TierlisteInternalImportHandler,
        @Optional()
        @Inject(LISTE_ABSCHLIESSEN_STEPPER_HANDLER)
        private listeAbschliessenStepperHandler?: ListeAbschliessenStepperHandler,
        @Optional()
        @Inject(RELEASE_NOTES_APP)
        private readonly releaseNotesApp?: 'desktop' | 'kundenportal',
    ) {
        this.checkImport();
    }

    /** Kundenportal: scans-first flow on all screen sizes (not the legacy upfront MatDialog). */
    private shouldUseKundenportalAusstallenFlow(): boolean {
        return this.releaseNotesApp === 'kundenportal';
    }

    /**
     * Gruppe-Details with pre-selected Tiere: skip upfront AusstallenDialog;
     * pick Zielbetrieb/Empfänger → create list → dashboard scan stepper.
     */
    private shouldUseScansFirstFromGruppeDetails(gruppeAbgangAlsDialog: boolean, tiere: GruppeTier[]): boolean {
        return (
            gruppeAbgangAlsDialog &&
            (tiere?.length ?? 0) > 0 &&
            !this.shouldUseKundenportalAusstallenFlow() &&
            !!this.listeAbschliessenStepperHandler
        );
    }

    private openAusstallenDialogOrScansFirst(
        gruppe: Gruppe | undefined,
        intern: boolean,
        extern: boolean,
        gruppeAbgangAlsDialog: boolean,
        tiere: GruppeTier[],
        aktuellerAusstallenderBetrieb: Client,
        zielbetrieb: Client | null,
        globalList = false,
    ): void {
        if (this.shouldUseScansFirstFromGruppeDetails(gruppeAbgangAlsDialog, tiere)) {
            this.startScansFirstAbgangFlow(
                gruppe,
                intern,
                extern,
                tiere,
                aktuellerAusstallenderBetrieb,
                zielbetrieb,
                globalList,
            );
            return;
        }
        this.openAusstallenDialogMitNachlauf(
            gruppe,
            intern,
            extern,
            gruppeAbgangAlsDialog,
            tiere,
            aktuellerAusstallenderBetrieb,
            zielbetrieb,
            globalList,
        );
    }

    getMobilePageContext(): AusstallenMobilePageContext | null {
        return this.mobilePageContext;
    }

    completeMobileAusstallenPage(result: AusstallenDialogResult | undefined): void {
        const ctx = this.mobilePageContext;
        this.mobilePageContext = null;
        if (!ctx) {
            return;
        }
        if (result) {
            this.handleAusstallenDialogResult(result, ctx);
        }
    }

    cancelMobileAusstallenPage(): void {
        this.mobilePageContext = null;
    }

    hasPendingMobileAusstallenFinish(): boolean {
        return !!this.pendingMobileAusstallenFinish;
    }

    clearPendingMobileAusstallenFinish(): void {
        this.pendingMobileAusstallenFinish = null;
    }

    /**
     * After mobile scan step: open full Ausstallen form to close the list (Liste abschließen).
     */
    tryOpenMobileAusstallenFinishPage(tierliste: Tierliste, gruppe?: Gruppe): boolean {
        if (!this.shouldUseKundenportalAusstallenFlow() || !tierliste?.tierliste_id) {
            return false;
        }
        const pending = this.pendingMobileAusstallenFinish;
        const intern =
            pending?.input?.intern ?? Number(tierliste.tierliste_extern ?? 0) === 0;
        const extern =
            pending?.input?.extern ?? Number(tierliste.tierliste_extern ?? 0) > 0;
        const input = pending
            ? new AusstallenDialogInput(pending.input.intern, pending.input.extern, { tierliste })
            : new AusstallenDialogInput(intern, extern, { tierliste });
        input.ausstallenderBetrieb =
            pending?.input?.ausstallenderBetrieb ?? this.stammdatenService.getAusgewaehlteVvvo();
        input.gruppe_abgang = gruppe ?? pending?.gruppe ?? pending?.input?.gruppe_abgang;
        input.zielbetrieb = pending?.input?.zielbetrieb;
        input.tiere = [];
        this.mobilePageContext = {
            input,
            mobilePhase: 'finish',
            gruppe: gruppe ?? pending?.gruppe,
        };
        void this.router.navigate(['/gruppen/ausstallen']);
        return true;
    }

    /**
     * Opens Ausstallen dialog (main app) or Kundenportal scans-first / finish-page route.
     */
    openAusstallenDialog(input: AusstallenDialogInput, context?: Omit<AusstallenMobilePageContext, 'input'>): void {
        if (this.shouldUseKundenportalAusstallenFlow()) {
            this.mobilePageContext = { input, ...context };
            void this.router.navigate(['/gruppen/ausstallen']);
            return;
        }

        this.dialog
            .open<AusstallenDialogComponent, AusstallenDialogInput, AusstallenDialogResult>(
                AusstallenDialogComponent,
                {
                    maxWidth: this.appService.isMobileScreen ? '95vw' : '50vw',
                    maxHeight: '90vh',
                    width: this.appService.isMobileScreen ? '95vw' : '50vw',
                    data: input,
                    disableClose: false,
                },
            )
            .afterClosed()
            .subscribe({
                next: (result: AusstallenDialogResult) => {
                    if (result) {
                        this.handleAusstallenDialogResult(result, context ?? {});
                    }
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
    }

    private handleAusstallenDialogResult(
        result: AusstallenDialogResult,
        ctx: Pick<
            AusstallenMobilePageContext,
            'gruppe' | 'gruppeAbgangAlsDialog' | 'tiere' | 'onComplete' | 'mobilePhase'
        >,
    ): void {
        if (ctx.onComplete) {
            ctx.onComplete(result);
            return;
        }
        if (ctx.mobilePhase === 'finish' && result?.tierliste) {
            this.pendingMobileAusstallenFinish = null;
            const liste = result.tierliste;
            if (Number(liste.status_id) === 162) {
                this.abgangService.setAbgangsliste(liste, false, true);
                this.abgangService.tierListeAbgeschlossen(liste, 'mobileAusstallenFinish');
                if (ctx.gruppe?.gruppe_id) {
                    this.abgangService.gruppeAktualisieren(ctx.gruppe.gruppe_id);
                }
                return;
            }
            this.closeAbgangTierliste(liste).subscribe({
                next: closed => {
                    const liste = closed ?? result.tierliste;
                    this.abgangService.setAbgangsliste(liste, false, true);
                    this.abgangService.tierListeAbgeschlossen(liste, 'mobileAusstallenFinish');
                    if (ctx.gruppe?.gruppe_id) {
                        this.abgangService.gruppeAktualisieren(ctx.gruppe.gruppe_id);
                    }
                },
                error: err => this.messageService.errorMessage('Fehler beim Abschließen der Ausstallliste!', err),
            });
            return;
        }
        if (result?.tierliste && ctx.gruppe) {
            this.gruppeAbgangOeffnen(
                ctx.gruppeAbgangAlsDialog ?? false,
                ctx.gruppe,
                result.tierliste,
                result?.gruppenTiere ?? ctx.tiere ?? [],
            );
            this.abgangService.setAbgangsliste(result.tierliste, true, true);
        }
    }

    public ausstallen(aktuelleGruppe: Gruppe, gruppeAbgangAlsDialog: boolean, tiere: GruppeTier[]) {
        const input = new AusstallenFrageDialogInput();
        input.gruppe = aktuelleGruppe;
        input.anzahl_tiere = tiere?.length;
        input.tiere = tiere;
        this.dialog
            .open<AusstallenFrageDialogComponent, AusstallenFrageDialogInput, AusstallenFrageDialogResult>(
                AusstallenFrageDialogComponent,
                { data: input, disableClose: false, minWidth: this.appService.isMobileScreen ? '95vw' : undefined },
            )
            .afterClosed()
            .subscribe({
                next: (result1: AusstallenFrageDialogResult) => {
                    if (result1?.ok) {
                        const tiereFuerListe = this.filterTiereFuerAbgangsliste(tiere);
                        if (result1.tierliste) {
                            this.openTierlisteAuswahl(aktuelleGruppe, gruppeAbgangAlsDialog, tiereFuerListe);
                        } else {
                            if (!aktuelleGruppe) {
                                const input = new GruppeAuswaehlenDialogInput();
                                input.mehrereGruppen = true;
                                input.subtitle = 'Gruppe zum Ausstallen wählen';
                                this.dialog
                                    .open<
                                        GruppeAuswaehlenDialogComponent,
                                        GruppeAuswaehlenDialogInput,
                                        GruppeAuswaehlenDialogResult
                                    >(
                                        GruppeAuswaehlenDialogComponent,
                                        buildGruppeAuswaehlenDialogConfig(input, {
                                            mobile: this.appService.isMobileScreen,
                                        }),
                                    )
                                    .afterClosed()
                                    .subscribe({
                                        next: (result2: GruppeAuswaehlenDialogResult) => {
                                            if (result2?.confirmed) {
                                                this.neueTierlisteErstellen(
                                                    result2.gruppe,
                                                    result1.intern,
                                                    result1.extern,
                                                    gruppeAbgangAlsDialog,
                                                    tiereFuerListe,
                                                    result2.ausstallenderBetrieb,
                                                    result2.mehrereGruppen,
                                                );
                                            }
                                        },
                                    });
                            } else {
                                this.neueTierlisteErstellen(
                                    aktuelleGruppe,
                                    result1.intern,
                                    result1.extern,
                                    gruppeAbgangAlsDialog,
                                    tiereFuerListe,
                                );
                            }
                        }
                    }
                },
            });
    }

    private neueTierlisteErstellen(
        gruppe: Gruppe | undefined,
        intern: boolean,
        extern: boolean,
        gruppeAbgangAlsDialog: boolean,
        tiere: GruppeTier[],
        ausstallenderBetrieb?: Client,
        globalList = false,
    ) {
        const aktuellerAusstallenderBetrieb = ausstallenderBetrieb ?? this.stammdatenService.getAusgewaehlteVvvo();
        this.openAusstallenDialogOrScansFirst(
            gruppe,
            intern,
            extern,
            gruppeAbgangAlsDialog,
            tiere,
            aktuellerAusstallenderBetrieb,
            null,
            globalList,
        );
    }

    private openAusstallenDialogMitNachlauf(
        gruppe: Gruppe | undefined,
        intern: boolean,
        extern: boolean,
        gruppeAbgangAlsDialog: boolean,
        tiere: GruppeTier[],
        aktuellerAusstallenderBetrieb: Client,
        zielbetrieb: Client | null,
        globalList = false,
    ): void {
        if (this.shouldUseKundenportalAusstallenFlow()) {
            this.startMobileAusstallenScansFirst(
                gruppe,
                intern,
                extern,
                tiere,
                aktuellerAusstallenderBetrieb,
                zielbetrieb,
                globalList,
            );
            return;
        }
        const input = new AusstallenDialogInput(intern, extern, {
            neu: { gruppe_abgang: gruppe, zielbetrieb },
        });
        input.ausstallenderBetrieb = aktuellerAusstallenderBetrieb;
        input.tiere = tiere;
        this.openAusstallenDialog(input, {
            gruppe,
            gruppeAbgangAlsDialog,
            tiere,
        });
    }

    /** Desktop Gruppe-Details: create list → Empfänger (extern) → scan stepper (settings at close). */
    private startScansFirstAbgangFlow(
        gruppe: Gruppe | undefined,
        intern: boolean,
        extern: boolean,
        tiere: GruppeTier[],
        aktuellerAusstallenderBetrieb: Client,
        zielbetrieb: Client | null,
        globalList = false,
    ): void {
        this.createMinimalAbgangTierliste(
            gruppe,
            intern,
            extern,
            aktuellerAusstallenderBetrieb,
            zielbetrieb,
            globalList,
        ).subscribe({
            next: liste => {
                if (tiere?.length > 0) {
                    this.tiereImportZuAbgangslisteHinzufuegen(gruppe, liste, tiere, {
                        extern,
                        continueAfterImport: true,
                    });
                    return;
                }
                this.continueAbgangAfterListeReady(gruppe, liste, extern);
            },
            error: err => {
                this.messageService.errorMessage('Ausstallliste konnte nicht erstellt werden!', err);
            },
        });
    }

    /** Kundenportal: group → scan step first, Ausstallen form only when closing the list. */
    private startMobileAusstallenScansFirst(
        gruppe: Gruppe | undefined,
        intern: boolean,
        extern: boolean,
        tiere: GruppeTier[],
        aktuellerAusstallenderBetrieb: Client,
        zielbetrieb: Client | null,
        globalList = false,
    ): void {
        const input = new AusstallenDialogInput(intern, extern, {
            neu: { gruppe_abgang: gruppe, zielbetrieb },
        });
        input.ausstallenderBetrieb = aktuellerAusstallenderBetrieb;
        input.tiere = tiere;
        this.pendingMobileAusstallenFinish = {
            input,
            gruppe,
            tiere,
            mobilePhase: 'finish',
        };
        this.createMinimalAbgangTierliste(
            gruppe,
            intern,
            extern,
            aktuellerAusstallenderBetrieb,
            zielbetrieb,
            globalList,
        ).subscribe({
            next: liste => {
                if (tiere?.length > 0) {
                    this.tiereImportZuAbgangslisteHinzufuegen(gruppe, liste, tiere, {
                        extern,
                        continueAfterImport: true,
                    });
                    return;
                }
                this.continueAbgangAfterListeReady(gruppe, liste, extern);
            },
            error: err => {
                this.pendingMobileAusstallenFinish = null;
                this.messageService.errorMessage('Ausstallliste konnte nicht erstellt werden!', err);
            },
        });
    }

    private createMinimalAbgangTierliste(
        gruppe: Gruppe | undefined,
        intern: boolean,
        extern: boolean,
        ausstallenderBetrieb?: Client,
        zielbetrieb?: Client | null,
        globalList = false,
    ): Observable<Tierliste> {
        const payload = new Tierliste();
        if (!globalList && gruppe?.gruppe_id) {
            payload.gruppe_id_abgang = gruppe.gruppe_id;
        }
        payload.mehrere_gruppen = globalList ? 1 : 0;
        payload.datum_abgang = new Date();
        payload.haendler_uebernehmen = 0;
        payload.tierliste_extern = extern ? 1 : 0;
        payload.umstallen = intern ? 1 : 0;
        if (ausstallenderBetrieb?.kunde_id) {
            payload.kunde_id_abgang = ausstallenderBetrieb.kunde_id;
        }
        if (intern && zielbetrieb?.kunde_id) {
            payload.kunde_id_einstallen = zielbetrieb.kunde_id;
            payload.tierliste_extern = resolveTierlisteExtern(payload, gruppe?.kunde_id);
        }
        return this.api.post(this.apiBaseUrl + 'tierliste/create_kundenportal.php', payload, undefined);
    }

    private continueAbgangAfterListeReady(
        gruppe: Gruppe | undefined,
        liste: Tierliste,
        extern: boolean,
    ): void {
        if (extern) {
            this.openAbgangZielAuswahlDialog(gruppe, liste, 'ausstallen_extern', updated =>
                this.continueAbgangAfterZielAuswahl(gruppe, updated),
            );
            return;
        }
        if (!Number(liste.kunde_id_einstallen)) {
            this.openAbgangZielAuswahlDialog(gruppe, liste, 'umstallen_intern', updated =>
                this.continueAbgangAfterZielAuswahl(gruppe, updated),
            );
            return;
        }
        this.continueAbgangAfterZielAuswahl(gruppe, liste);
    }

    private continueAbgangAfterZielAuswahl(gruppe: Gruppe | undefined, liste: Tierliste): void {
        if (this.shouldUseKundenportalAusstallenFlow()) {
            this.navigateMobileAbgangScans(gruppe, liste);
            return;
        }
        this.openAbgangScansStepper(gruppe, liste);
    }

    private openAbgangZielAuswahlDialog(
        gruppe: Gruppe | undefined,
        liste: Tierliste,
        flowType: AbgangZielFlowType,
        onDone: (liste: Tierliste) => void,
    ): void {
        const inhaberKundeId = Number(gruppe?.kunde_id) || null;
        this.dialog
            .open(AusstallenEmpfaengerDialogComponent, {
                width: '760px',
                maxWidth: 'calc(100vw - 24px)',
                maxHeight: '90vh',
                panelClass: ['ausstallen-empfaenger-dialog-panel', 'letzte-aktionen-empfaenger-dialog-panel'],
                data: {
                    tierliste: liste,
                    gruppeKundeId: gruppe?.gruppe_id,
                    flowType,
                    inhaberKundeId,
                    showDatumAbgang: this.shouldUseKundenportalAusstallenFlow(),
                },
            })
            .afterClosed()
            .subscribe({
                next: (result: AusstallenEmpfaengerDialogResult | undefined) => {
                    if (!result) {
                        if (this.shouldUseKundenportalAusstallenFlow()) {
                            this.pendingMobileAusstallenFinish = null;
                        }
                        return;
                    }
                    onDone(result.tierliste);
                },
            });
    }

    private openEmpfaengerBeforeAbgangScans(gruppe: Gruppe | undefined, liste: Tierliste): void {
        this.openAbgangZielAuswahlDialog(gruppe, liste, 'ausstallen_extern', updated => {
            this.continueAbgangAfterZielAuswahl(gruppe, updated);
        });
    }

    private navigateMobileAbgangScans(gruppe: Gruppe | undefined, liste: Tierliste): void {
        this.abgangService.setAbgangsliste(liste, true, true);
        if (gruppe?.gruppe_id) {
            void this.router.navigate(['/gruppen/abgang', gruppe.gruppe_id], {
                queryParams: { tierliste_id: liste.tierliste_id },
            });
            return;
        }
        void this.router.navigate(['/gruppen/abgang'], {
            queryParams: { tierliste_id: liste.tierliste_id },
        });
    }

    /** Same close payload as AbgangslisteSchliessenDialog (status 162). */
    private closeAbgangTierliste(tierliste: Tierliste): Observable<Tierliste> {
        const payload: Tierliste = ohneTierlisteKommentar({ ...tierliste });
        payload.status_id = 162;
        payload.haendler_uebernehmen = 0;
        const istDerselbeKunde = resolveTierlisteExtern(payload) === 0;
        payload.tierliste_extern = resolveTierlisteExtern(payload);

        return forkJoin({
            hitMelden: this.api.getSpeicher('hit_melden').pipe(catchError(() => of(1))),
            gleicheUnternehmensgruppe: this.stammdatenService.istBetriebInKundenportal(
                Number(payload.kunde_id_einstallen || 0),
            ),
        }).pipe(
            switchMap(({ hitMelden, gleicheUnternehmensgruppe }) => {
                if (istDerselbeKunde) {
                    payload.abgang_melden = 0;
                } else if (payload.abgang_melden === undefined || payload.abgang_melden === null) {
                    payload.abgang_melden =
                        hitMelden != null && hitMelden !== undefined ? Number(hitMelden) : 1;
                } else {
                    payload.abgang_melden = Number(payload.abgang_melden);
                }

                payload.import_einstallen = payload.kunde_id_einstallen ? 1 : 0;

                if ((istDerselbeKunde || gleicheUnternehmensgruppe) && payload.kunde_id_einstallen) {
                    payload.reklamationen_weitergeben = 1;
                } else {
                    payload.reklamationen_weitergeben = 0;
                }

                return this.api
                    .post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', payload, undefined)
                    .pipe(map(res => ({ ...tierliste, ...(res ?? payload) })));
            }),
        );
    }

    private tierlistePerEmailSenden(tierliste_id: number, kunde_id_schlachthof: number): Observable<boolean> {
        const subject = new Subject<boolean>();
        let params = new HttpParams();
        params = params.set('tierliste_id', tierliste_id);
        params = params.set('kunde_id_schlachthof', kunde_id_schlachthof);
        this.api.get(this.apiBaseUrl + 'tierliste/mail_schlachthof.php', params).subscribe({
            next: () => {
                this.messageService.infoMessage('Email erfolgreich gesendet!');
                subject.next(true);
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                subject.next(false);
            },
        });
        return subject;
    }

    public tierlisteAnSchlachthofSenden(tierliste: Tierliste): Observable<boolean> {
        const subject = new Subject<boolean>();
        if (tierliste.kunde_id_schlachthof) {
            const input = new ConfirmDialogInput();
            input.headline = 'Liste senden';
            input.text = 'Soll die Tierliste per Mail an den Schlachthof gesendet werden?';
            input.okButton = 'Senden';

            this.dialog
                .open(ConfirmDialogComponent, { data: input })
                .afterClosed()
                .subscribe({
                    next: (response: ConfirmDialogResult) => {
                        if (response?.confirmed) {
                            this.tierlistePerEmailSenden(
                                tierliste.tierliste_id,
                                tierliste.kunde_id_schlachthof,
                            ).subscribe({
                                next: (result: boolean) => {
                                    subject.next(result);
                                },
                            });
                        } else {
                            subject.next(false);
                        }
                    },
                });
        } else {
            this.dialog
                .open(TierlisteSchlachthofAuswahlDialogComponent)
                .afterClosed()
                .subscribe({
                    next: (schlachthof: Client) => {
                        if (schlachthof) {
                            return this.tierlistePerEmailSenden(tierliste.tierliste_id, schlachthof.kunde_id).subscribe(
                                {
                                    next: (result: boolean) => {
                                        subject.next(result);
                                    },
                                },
                            );
                        } else {
                            subject.next(false);
                        }
                    },
                });
        }
        return subject;
    }

    private tiereImportZuAbgangslisteHinzufuegen(
        gruppe: Gruppe,
        tierliste: Tierliste,
        tiere: GruppeTier[],
        options?: { extern?: boolean; continueAfterImport?: boolean },
    ) {
        const obj = {
            tierliste_id: tierliste?.tierliste_id,
            gruppen_tiere: tiere,
            gruppe_id: gruppe?.gruppe_id ?? undefined,
        };
        const snackbarRef = this.messageService.infoMessage(
            'Tiere werden zur Ausstallliste hinzugefügt. Dieses kann einen Moment dauern',
        );
        this.api.post(this.apiBaseUrl + 'tierliste/abgang_kundenportal.php', obj, undefined).subscribe({
            next: (tierlisteAntwort: Tierliste) => {
                //snackbarRef.dismiss();
                this.messageService.infoMessage(
                    tiere?.length + ' Tiere wurden zur aktuellen Ausstallliste hinzugefügt.',
                );
                // Gruppentier-Liste auf der Seite neu laden, von der aus der Dialog geöffnet wurde
                if (gruppe?.gruppe_id) {
                    this.abgangService.gruppeAktualisieren(gruppe.gruppe_id);
                }
                const extern =
                    options?.extern ??
                    this.pendingMobileAusstallenFinish?.input?.extern ??
                    Number(tierlisteAntwort?.tierliste_extern) > 0;
                if (options?.continueAfterImport) {
                    this.continueAbgangAfterListeReady(gruppe, tierlisteAntwort, extern);
                    return;
                }
                if (this.shouldUseKundenportalAusstallenFlow() && this.pendingMobileAusstallenFinish) {
                    this.continueAbgangAfterListeReady(gruppe, tierlisteAntwort, extern);
                    return;
                }
                this.openAbgangScansStepper(gruppe, tierlisteAntwort);
            },
            error: err => {
                //snackbarRef.dismiss();
                this.messageService.errorMessage('Fehler beim Hinzufügen der Tiere!', err);
                this.openAbgangScansStepper(gruppe, tierliste);
            },
        });
    }

    private gruppeAbgangDialogOeffnen(gruppe: Gruppe, liste: Tierliste, tiere: GruppeTier[]) {
        if (liste && tiere?.length > 0) {
            this.tiereImportZuAbgangslisteHinzufuegen(gruppe, liste, tiere);
            return;
        }
        this.openAbgangScansStepper(gruppe, liste);
    }

    /** Opens dashboard Tiere-auswählen stepper (abgang-tier-eingabe), not legacy GruppeAbgangDialog. */
    private openAbgangScansStepper(gruppe: Gruppe, liste: Tierliste): void {
        if (this.listeAbschliessenStepperHandler && liste?.tierliste_id) {
            this.abgangService.setAbgangsliste(liste, true, true);
            this.listeAbschliessenStepperHandler.open(liste, { initialStep: 'tiere' }).subscribe({
                next: saved => {
                    if (saved) {
                        this.abgangService.tierListeAbgeschlossen(liste, 'openAbgangScansStepper');
                    }
                    if (gruppe?.gruppe_id) {
                        this.abgangService.gruppeAktualisieren(gruppe.gruppe_id);
                    }
                },
            });
            return;
        }
        const input = new GruppeAbgangDialogInput(liste, true, gruppe);
        const dialogRefAbgang = this.dialog.open<GruppeAbgangDialogComponent, GruppeAbgangDialogInput, Tierliste>(
            GruppeAbgangDialogComponent,
            { data: input, disableClose: false, maxHeight: '95vh' },
        );
        dialogRefAbgang.afterClosed().subscribe({
            next: tierliste => {
                if (tierliste) {
                    this.abgangService.tierListeAbgeschlossen(tierliste, 'gruppeAbgangDialogOeffnen');
                }
            },
        });
    }

    private gruppeAbgangOeffnen(gruppeAbgangAlsDialog: boolean, gruppe: Gruppe, liste: Tierliste, tiere: GruppeTier[]) {
        if (gruppeAbgangAlsDialog) {
            this.gruppeAbgangDialogOeffnen(gruppe, liste, tiere);
        } else {
            if (gruppe) {
                this.router
                    .navigate(['/gruppen/abgang', gruppe.gruppe_id], {
                        queryParams: { tierliste_id: liste.tierliste_id },
                    })
                    .then();
            } else {
                this.router.navigate(['/gruppen/abgang'], { queryParams: { tierliste_id: liste.tierliste_id } }).then();
            }
        }
    }

    private filterTiereFuerAbgangsliste(tiere: GruppeTier[]): GruppeTier[] {
        return (tiere || []).filter(
            tier =>
                !!tier?.gt_id &&
                !tier.tierliste_id &&
                tier.hit_todesdatum === null &&
                tier.kp_todesdatum === null &&
                tier.hit_abgangsdatum === null,
        );
    }

    private openTierlisteAuswahl(aktuelleGruppe: Gruppe, gruppeAbgangAlsDialog: boolean, tiere: GruppeTier[]) {
        const input = new TierlistenauswahlDialogInput();
        input.gruppe = aktuelleGruppe;
        input.kunde_id = Number(
            aktuelleGruppe?.kunde_id || this.stammdatenService.getAusgewaehlteVvvo()?.kunde_id || 0,
        ) || undefined;
        input.onlyActive = true;
        this.dialog
            .open<TierlistenauswahlDialogComponent, TierlistenauswahlDialogInput, Tierliste>(
                TierlistenauswahlDialogComponent,
                { data: input, disableClose: true },
            )
            .afterClosed()
            .subscribe({
                next: (result: Tierliste) => {
                    if (result) {
                        this.gruppeAbgangOeffnen(gruppeAbgangAlsDialog, aktuelleGruppe, result, tiere);
                    }
                },
            });
    }

    /** After internal Ausstall/Umstallen close: Gruppe wählen → Import-Optionen when target is the same kunde or an own/shared betrieb. */
    triggerInternalImportIfEligible(tierliste: Tierliste | null | undefined): void {
        const zielKundeId = Number(tierliste?.kunde_id_einstallen || 0);
        const abgangKundeId = Number(tierliste?.kunde_id_abgang || 0);
        const sameBetrieb = zielKundeId > 0 && abgangKundeId > 0 && zielKundeId === abgangKundeId;
        if (!tierliste?.tierliste_id || zielKundeId <= 0 || this.importDialogOffen) {
            return;
        }

        if (sameBetrieb && this.internalImportHandler) {
            this.importDialogOffen = true;
            this.internalImportHandler
                .open(tierliste)
                .pipe(finalize(() => (this.importDialogOffen = false)))
                .subscribe();
            return;
        }

        this.importDialogOffen = true;
        this.stammdatenService.ladeMeineUndFreigegebeneVvvos().subscribe({
            next: response => {
                const meinBetrieb = (response?.records ?? []).find(
                    vvvo => Number(vvvo?.kunde_id) === zielKundeId,
                );
                if (meinBetrieb && this.internalImportHandler) {
                    this.internalImportHandler
                        .open(tierliste)
                        .pipe(finalize(() => (this.importDialogOffen = false)))
                        .subscribe();
                } else {
                    this.importDialogOffen = false;
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                this.importDialogOffen = false;
            },
        });
    }

    private checkImport() {
        this.abgangService.watchTierListeAbgeschlossen().subscribe({
            next: tierliste => this.triggerInternalImportIfEligible(tierliste),
        });
    }
}
